import { TabPane, Dialog, Button, Toast, Pane, Text, PropertyGrid, CompositeTextInput, MultiSelector, TextInput, Flex } from '@leverege/ui-elements';
import { Validator } from '@leverege/validator';
import React, { useCallback, useRef, useState } from 'react';
import I18N from '@leverege/i18n';
import { GlobalState } from '@leverege/ui-redux';
import classnames from 'classnames';
import S from './ShareTemporaryUserAccess.css';
import Icons from "../../Icons.js";
const {
  t
} = I18N.ns('temporaryUserManagement.ShareDialog');
export default function ShareTemporaryUserAccess(props) {
  const {
    show,
    actions,
    temporaryUser,
    onClose,
    emailOptions,
    smsOptions,
    uiName,
    uiUrl
  } = props;
  const [email, setEmail] = useState([]);
  const [multiEmailInputError, setMultiEmailInputError] = useState(false);
  const [multiSmsInputError, setMultiSmsInputError] = useState(false);
  const multiEmailRef = useRef();
  const multiSmsRef = useRef();
  const [sms, setSms] = useState([]);
  const [additionalEmailMsg, setAdditionalEmailMsg] = useState('');
  const [additionalSmsMsg, setAdditionalSmsMsg] = useState('');
  const sendEmails = useCallback(async () => {
    try {
      let resolvedUiName;
      if (typeof uiName === 'function') {
        resolvedUiName = uiName({
          user: temporaryUser,
          methods: ['email']
        });
      } else {
        resolvedUiName = emailOptions?.uiName || uiName;
      }
      await GlobalState.dispatch(actions.temporaryUser(temporaryUser).share({
        accessCode: temporaryUser.accessCode,
        emailOptions: {
          ...emailOptions,
          uiName: resolvedUiName,
          message: additionalEmailMsg
        },
        email
      }));
      Toast.success(t('emailSuccess'));
      setEmail([]);
    } catch (err) {
      console.error(err);
      Toast.error(t('emailError'));
    }
  }, [email, actions, temporaryUser, additionalEmailMsg, emailOptions, uiName]);
  const sendSms = useCallback(async () => {
    try {
      let resolvedUiName;
      if (typeof uiName === 'function') {
        resolvedUiName = uiName({
          user: temporaryUser,
          methods: ['sms']
        });
      } else {
        resolvedUiName = smsOptions?.uiName || uiName;
      }
      await GlobalState.dispatch(actions.temporaryUser(temporaryUser).share({
        accessCode: temporaryUser.accessCode,
        smsOptions: {
          ...smsOptions,
          uiName: resolvedUiName,
          message: additionalSmsMsg
        },
        phone: sms
      }));
      Toast.success(t('smsSuccess'));
      setSms([]);
    } catch (err) {
      console.error(err);
      Toast.error(t('smsErr'));
    }
  }, [sms, actions, temporaryUser, additionalSmsMsg, smsOptions, uiName]);
  const wSetEmail = useCallback(({
    value
  }) => {
    setEmail(value);
    setMultiEmailInputError(false);
  }, [setEmail, setMultiEmailInputError]);
  const wSetSms = useCallback(({
    value
  }) => {
    setSms(value);
    setMultiSmsInputError(false);
  }, [setSms]);
  const wSetAdditionalEmailMsg = useCallback(({
    value
  }) => setAdditionalEmailMsg(value), [setAdditionalEmailMsg]);
  const wSetAdditionalSmsMsg = useCallback(({
    value
  }) => setAdditionalSmsMsg(value), [setAdditionalSmsMsg]);
  const copyToClipboard = useCallback(({
    data: {
      value,
      field
    }
  }) => {
    navigator.clipboard.writeText(value);
    Toast.success(t('clipboard', {
      name: t(field)
    }));
  }, []);
  let resolvedUiUrl = uiUrl;
  if (typeof uiUrl === 'function') {
    resolvedUiUrl = uiUrl({
      user: temporaryUser,
      methods: ['link']
    });
  }
  const linkValue = t('link', {
    temporaryUser,
    uiUrl: resolvedUiUrl
  });
  const linkNoCodeValue = t('linkNoCode', {
    temporaryUser,
    uiUrl: resolvedUiUrl
  });
  const accessCodeValue = temporaryUser.accessCode;
  const emailValid = email.every(Validator.isEmail);
  const smsValid = sms.every(Validator.isPhone);
  const tuWithDates = {
    ...temporaryUser,
    expiresAt: new Date(temporaryUser.expiresAt),
    validAfter: new Date(temporaryUser.validAfter)
  };
  return /*#__PURE__*/React.createElement(Dialog.Message, {
    variant: "editorDialog",
    bodyVariant: "shareTempUserBody|formBody",
    headerVariant: "shareTempUserHeader|userEditorHeader|rootEditorHeader|formHeader",
    footerVariant: "shareTempUserFooter|userEditorFooter|rootEditorFooter|formFooter",
    dialogTitleVariant: "shareTempUserTitle|userEditorTitle|rootEditorTitle|formTitle",
    footerClassName: S.footer,
    bodyClassName: S.shareTempUserBody,
    icon: Icons.temporaryUserScreenIcon,
    title: t('title'),
    onClose: onClose,
    show: show,
    okay: t('okay'),
    okayVariant: "tempUserShareOkay|dialog"
  }, /*#__PURE__*/React.createElement(Pane, null, /*#__PURE__*/React.createElement(TabPane, {
    variant: "tempUser",
    className: S.shareTabPane,
    defaultId: "email"
  }, /*#__PURE__*/React.createElement(TabPane.Tab, {
    className: S.linkTab,
    tabId: "link",
    tabName: "Link"
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colS",
    className: S.tab
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "shareTempUser|editor",
    vertical: true
  }, null, /*#__PURE__*/React.createElement(Text, null, t('linkMessage', tuWithDates)), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('linkLabel')
  }, /*#__PURE__*/React.createElement(Pane, {
    layout: "flexColS"
  }, /*#__PURE__*/React.createElement(CompositeTextInput, {
    post: /*#__PURE__*/React.createElement(Button, {
      variant: "copyToClipboardIcon",
      icon: "fa-kit fa-lv-copy",
      onClick: copyToClipboard,
      eventData: {
        value: linkValue,
        field: 'linkLabel'
      }
    }),
    value: linkValue,
    disabled: true
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('linkCaption', tuWithDates)))), /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('accessDetails')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('accessCodeLabel')
  }, /*#__PURE__*/React.createElement(Pane, {
    layout: "flexColS"
  }, /*#__PURE__*/React.createElement(CompositeTextInput, {
    post: /*#__PURE__*/React.createElement(Button, {
      variant: "copyToClipboardIcon",
      icon: "fa-kit fa-lv-copy",
      onClick: copyToClipboard,
      eventData: {
        value: accessCodeValue,
        field: 'accessCodeLabel'
      }
    }),
    value: accessCodeValue,
    disabled: true
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('accessCodeCaptionPrefix', tuWithDates), /*#__PURE__*/React.createElement("strong", null, t('accessCodeCaption', tuWithDates)), t('accessCodeCaptionSuffix', tuWithDates)))), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('linkNoCodeLabel')
  }, /*#__PURE__*/React.createElement(Pane, {
    layout: "flexColS"
  }, /*#__PURE__*/React.createElement(CompositeTextInput, {
    post: /*#__PURE__*/React.createElement(Button, {
      variant: "copyToClipboardIcon",
      icon: "fa-kit fa-lv-copy",
      onClick: copyToClipboard,
      eventData: {
        value: linkNoCodeValue,
        field: 'linkNoCodeLabel'
      }
    }),
    value: linkNoCodeValue,
    disabled: true
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('linkNoCodeCaption', tuWithDates))))))), /*#__PURE__*/React.createElement(TabPane.Tab, {
    className: S.emailTab,
    tabId: "email",
    tabName: "Email"
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colS",
    className: S.tab
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "shareTempUser|editor",
    vertical: true
  }, emailValid ? null : /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "tempUserShareError"
  }, t('emailInvalid'))), /*#__PURE__*/React.createElement(Text, null, t('emailMessage', tuWithDates)), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('emailLabel')
  }, /*#__PURE__*/React.createElement(Pane, null, /*#__PURE__*/React.createElement(MultiSelector, {
    ref: multiEmailRef,
    placeholder: t('emailPlaceholder'),
    key: "emailInput",
    variant: "userFormInput|formInput",
    className: multiEmailInputError || !emailValid ? classnames(S.multiEmail, S.multiError) : S.multiEmail,
    selectionType: "tags",
    onChange: wSetEmail,
    value: email,
    values: email,
    inputProps: {
      onBlur: () => {
        setMultiEmailInputError(!!multiEmailRef.current?.state?.filterTerm);
      }
    },
    allowCreate: true,
    clearSearchOnSelect: true
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('emailCaption')))), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('additionalMsg')
  }, /*#__PURE__*/React.createElement(Pane, null, /*#__PURE__*/React.createElement(TextInput, {
    hint: t('additionalMsgPlaceholder'),
    value: additionalEmailMsg,
    onChange: wSetAdditionalEmailMsg
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('additionalEmailMsgCaption')))), /*#__PURE__*/React.createElement(Button, {
    disabled: !email?.length || !emailValid,
    variant: "secondary",
    onClick: sendEmails
  }, t('sendEmail'))))), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "sms",
    tabName: "SMS"
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colS",
    className: S.tab
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "shareTempUser|editor",
    vertical: true
  }, smsValid ? null : /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "tempUserShareError"
  }, t('smsInvalid'))), /*#__PURE__*/React.createElement(Text, null, t('smsMessage', tuWithDates)), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('smsLabel')
  }, /*#__PURE__*/React.createElement(Pane, null, /*#__PURE__*/React.createElement(MultiSelector, {
    ref: multiSmsRef,
    placeholder: t('smsPlaceholder'),
    key: "smsInput",
    variant: "userFormInput|formInput",
    className: multiSmsInputError || !smsValid ? classnames(S.multiSms, S.multiError) : S.multiSms,
    selectionType: "tags",
    onChange: wSetSms,
    value: sms,
    values: sms,
    inputProps: {
      onBlur: () => {
        setMultiSmsInputError(!!multiSmsRef.current?.state?.filterTerm);
      }
    },
    allowCreate: true,
    clearSearchOnSelect: true
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('smsCaption')))), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    stretch: true,
    label: t('additionalMsg')
  }, /*#__PURE__*/React.createElement(Pane, null, /*#__PURE__*/React.createElement(TextInput, {
    hint: t('additionalMsgPlaceholder'),
    value: additionalSmsMsg,
    onChange: wSetAdditionalSmsMsg
  }), /*#__PURE__*/React.createElement(Text, {
    variant: "inputCaption"
  }, t('additionalSmsMsgCaption')))), /*#__PURE__*/React.createElement(Button, {
    disabled: !sms?.length || !smsValid,
    variant: "secondary",
    onClick: sendSms
  }, t('sendSms'))))))));
}